import { useCallback, useEffect, useMemo, useState } from "react";
import {
    createCatalogCategories,
    createCatalogProducts,
    getCatalogCategories,
    getCatalogProducts,
    getCatalogRestaurants,
    updateCatalogCategories,
    updateCatalogProducts,
} from "../api/catalogApi";

const blankProduct = { name: "", price: "", gstRate: "5", preferenceOrder: "", colorcode: "" };
const blankCategory = { name: "", gstRate: "5", preferenceOrder: "", colorcode: "" };

const money = (value) => new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
}).format(Number(value || 0));

const selectionKey = (row, kind) => `${row.restaurantId}:${kind === "products" ? row.productId : row.categoryId}`;

export default function CatalogManager({ kind }) {
    const isProducts = kind === "products";
    const [restaurants, setRestaurants] = useState([]);
    const [restaurantFilter, setRestaurantFilter] = useState("all");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("active");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [rows, setRows] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [formMode, setFormMode] = useState(null);
    const [editingRow, setEditingRow] = useState(null);
    const [form, setForm] = useState(isProducts ? blankProduct : blankCategory);
    const [targetRestaurantIds, setTargetRestaurantIds] = useState([]);
    const [categoryMappings, setCategoryMappings] = useState({});
    const [bulkFields, setBulkFields] = useState({ name: "", price: "", gstRate: "" });

    const categoryRowsForSelection = useMemo(() => {
        if (restaurantFilter === "all") return categories;
        return categories.filter((category) => String(category.restaurantId) === String(restaurantFilter));
    }, [categories, restaurantFilter]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 250);
        return () => clearTimeout(timer);
    }, [search]);

    const loadCatalog = useCallback(async (signal) => {
        setLoading(true);
        setError("");
        try {
            const [restaurantRows, categoryRows, productRows] = await Promise.all([
                getCatalogRestaurants(),
                getCatalogCategories(restaurantFilter),
                isProducts
                    ? getCatalogProducts({ restaurantId: restaurantFilter, categoryId: categoryFilter, status: statusFilter, search: debouncedSearch.trim() })
                    : Promise.resolve([]),
            ]);
            if (signal?.aborted) return;
            setRestaurants(restaurantRows);
            setCategories(categoryRows);
            setRows(isProducts ? productRows : categoryRows.filter((category) => {
                const matchesStatus = statusFilter === "all" || category.status === statusFilter;
                const matchesSearch = !debouncedSearch.trim() || `${category.name} ${category.restaurantName}`.toLowerCase().includes(debouncedSearch.trim().toLowerCase());
                return matchesStatus && matchesSearch;
            }));
        } catch (requestError) {
            if (signal?.aborted || requestError.code === "ERR_CANCELED") return;
            setError(requestError?.response?.data?.message || `Unable to load ${isProducts ? "products" : "categories"}.`);
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    }, [restaurantFilter, categoryFilter, statusFilter, debouncedSearch, isProducts]);

    useEffect(() => {
        const controller = new AbortController();
        loadCatalog(controller.signal);
        return () => controller.abort();
    }, [loadCatalog]);

    const closeForm = () => {
        setFormMode(null);
        setEditingRow(null);
        setTargetRestaurantIds([]);
        setCategoryMappings({});
        setBulkFields({ name: "", price: "", gstRate: "" });
    };

    const beginCreate = () => {
        if (restaurantFilter === "all" && restaurants.length === 0) {
            setError("No restaurants are available to create catalog records.");
            return;
        }
        setError("");
        setForm(isProducts ? blankProduct : blankCategory);
        setTargetRestaurantIds(restaurantFilter === "all" ? [] : [Number(restaurantFilter)]);
        setCategoryMappings({});
        setFormMode("create");
    };

    const beginEdit = (row) => {
        setError("");
        setEditingRow(row);
        setForm(isProducts
            ? { name: row.name || "", price: String(row.price ?? ""), gstRate: String(row.gstRate ?? 0), preferenceOrder: String(row.preferenceOrder ?? ""), colorcode: row.colorcode || "", categoryId: String(row.categoryId || "") }
            : { name: row.name || "", gstRate: String(row.gstRate ?? 0), preferenceOrder: String(row.preferenceOrder ?? ""), colorcode: row.colorcode || "" });
        setFormMode("edit");
    };

    const toggleSelection = (row) => {
        const key = selectionKey(row, kind);
        setSelected((current) => current.includes(key) ? current.filter((item) => item !== key) : [...current, key]);
    };

    const visibleTargets = rows.filter((row) => selected.includes(selectionKey(row, kind)));
    const targetsPayload = visibleTargets.map((row) => ({
        restaurantId: row.restaurantId,
        [isProducts ? "productId" : "categoryId"]: isProducts ? row.productId : row.categoryId,
    }));

    const toggleTargetRestaurant = (restaurantId) => {
        setTargetRestaurantIds((current) => current.includes(restaurantId)
            ? current.filter((id) => id !== restaurantId)
            : [...current, restaurantId]);
    };

    const handleCreate = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError("");
        setNotice("");
        try {
            if (!targetRestaurantIds.length) throw new Error("Select at least one restaurant.");
            if (isProducts) {
                const mappings = targetRestaurantIds.map((restaurantId) => ({
                    restaurantId,
                    categoryId: Number(categoryMappings[restaurantId]),
                }));
                if (mappings.some((mapping) => !mapping.categoryId)) throw new Error("Choose a category for each selected restaurant.");
                await createCatalogProducts({ ...form, categoryMappings: mappings });
            } else {
                await createCatalogCategories({ ...form, restaurantIds: targetRestaurantIds });
            }
            setNotice(`${isProducts ? "Product" : "Category"} created for ${targetRestaurantIds.length} restaurant${targetRestaurantIds.length === 1 ? "" : "s"}.`);
            closeForm();
            setSelected([]);
            await loadCatalog();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || requestError.message || "Unable to create catalog records.");
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async (event) => {
        event.preventDefault();
        if (!editingRow) return;
        setSaving(true);
        setError("");
        setNotice("");
        try {
            const updates = { ...form };
            delete updates.categoryId;
            if (isProducts) updates.categoryId = Number(form.categoryId);
            await (isProducts ? updateCatalogProducts : updateCatalogCategories)({
                targets: [{
                    restaurantId: editingRow.restaurantId,
                    [isProducts ? "productId" : "categoryId"]: isProducts ? editingRow.productId : editingRow.categoryId,
                }],
                updates,
            });
            setNotice(`${isProducts ? "Product" : "Category"} updated.`);
            closeForm();
            await loadCatalog();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || "Unable to update this record.");
        } finally {
            setSaving(false);
        }
    };

    const handleBatchEdit = async (event) => {
        event.preventDefault();
        const updates = {};
        for (const field of ["name", "price", "gstRate"]) {
            if (bulkFields[field] !== "") updates[field] = field === "name" ? bulkFields[field] : Number(bulkFields[field]);
        }
        if (!Object.keys(updates).length) return setError("Enter at least one field to update.");
        setSaving(true);
        setError("");
        setNotice("");
        try {
            await (isProducts ? updateCatalogProducts : updateCatalogCategories)({ targets: targetsPayload, updates });
            setNotice(`${targetsPayload.length} ${isProducts ? "products" : "categories"} updated.`);
            closeForm();
            setSelected([]);
            await loadCatalog();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || "Unable to update selected records.");
        } finally {
            setSaving(false);
        }
    };

    const handleBatchStatus = async (status) => {
        if (!targetsPayload.length) return;
        const verb = status === "deactive" ? "archive" : "restore";
        if (!window.confirm(`${verb === "archive" ? "Archive" : "Restore"} ${targetsPayload.length} selected ${isProducts ? "products" : "categories"}?`)) return;
        setSaving(true);
        setError("");
        setNotice("");
        try {
            await (isProducts ? updateCatalogProducts : updateCatalogCategories)({ targets: targetsPayload, updates: { status } });
            setNotice(`${targetsPayload.length} ${isProducts ? "products" : "categories"} ${status === "active" ? "restored" : "archived"}.`);
            setSelected([]);
            await loadCatalog();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || "Unable to update selected statuses.");
        } finally {
            setSaving(false);
        }
    };

    const allVisibleSelected = rows.length > 0 && rows.every((row) => selected.includes(selectionKey(row, kind)));
    const toggleAllVisible = () => setSelected(allVisibleSelected
        ? selected.filter((key) => !rows.some((row) => selectionKey(row, kind) === key))
        : [...new Set([...selected, ...rows.map((row) => selectionKey(row, kind))])]);

    const activeCategoriesForRestaurant = (restaurantId, includeCategoryId = null) => categories.filter((category) =>
        Number(category.restaurantId) === Number(restaurantId) &&
        (category.status === "active" || Number(category.categoryId) === Number(includeCategoryId))
    );

    const editFields = (
        <>
            <label className="block text-sm text-slate-300">Name<input required maxLength={100} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>
            {isProducts && <label className="block text-sm text-slate-300">Price (whole INR)<input required type="number" min="1" step="1" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>}
            {isProducts && formMode === "edit" && <label className="block text-sm text-slate-300">Category<select required value={form.categoryId || ""} onChange={(event) => setForm({ ...form, categoryId: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white">{activeCategoriesForRestaurant(editingRow?.restaurantId, editingRow?.categoryId).map((category) => <option key={category.categoryId} value={category.categoryId}>{category.name}{category.status === "deactive" ? " (archived)" : ""}</option>)}</select></label>}
            <label className="block text-sm text-slate-300">GST rate (%)<input required type="number" min="0" max="100" step="1" value={form.gstRate} onChange={(event) => setForm({ ...form, gstRate: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>
            <label className="block text-sm text-slate-300">Preference order<input type="number" min="1" step="1" value={form.preferenceOrder} onChange={(event) => setForm({ ...form, preferenceOrder: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>
            <label className="block text-sm text-slate-300">Color code<input maxLength={20} value={form.colorcode} onChange={(event) => setForm({ ...form, colorcode: event.target.value })} placeholder="#22aaff" className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>
        </>
    );

    return (
        <main className="min-h-[calc(100vh-178px)] bg-slate-950 px-4 py-5 text-slate-100 md:px-6">
            <div className="mx-auto flex max-w-7xl flex-col gap-5">
                <header className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-900 p-5 lg:flex-row lg:items-end">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-300">SuperAdmin catalog</p>
                        <h1 className="mt-1 text-2xl font-semibold text-white">{isProducts ? "Products" : "Product categories"}</h1>
                        <p className="mt-1 text-sm text-slate-400">{isProducts ? "Manage menu products across outlets." : "Manage categories and their GST settings across outlets."}</p>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <label className="flex flex-col gap-1 text-xs text-slate-400">Restaurant
                            <select value={restaurantFilter} onChange={(event) => { setRestaurantFilter(event.target.value); setSelected([]); setCategoryFilter("all"); }} className="min-w-44 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white">
                                <option value="all">All restaurants</option>
                                {restaurants.map((restaurant) => <option key={restaurant.id} value={restaurant.id}>{restaurant.name}</option>)}
                            </select>
                        </label>
                        {isProducts && <label className="flex flex-col gap-1 text-xs text-slate-400">Category
                            <select value={categoryFilter} onChange={(event) => { setCategoryFilter(event.target.value); setSelected([]); }} className="min-w-40 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white">
                                <option value="all">All categories</option>
                                {categoryRowsForSelection.map((category) => <option key={`${category.restaurantId}:${category.categoryId}`} value={category.categoryId}>{restaurantFilter === "all" ? `${category.name} · ${category.restaurantName}` : category.name}</option>)}
                            </select>
                        </label>}
                        <label className="flex flex-col gap-1 text-xs text-slate-400">Status
                            <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setSelected([]); }} className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white">
                                <option value="active">Active</option><option value="deactive">Archived</option><option value="all">All</option>
                            </select>
                        </label>
                        <button type="button" onClick={beginCreate} className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-500">+ Add {isProducts ? "product" : "category"}</button>
                    </div>
                </header>

                {error && <div role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</div>}
                {notice && <div role="status" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{notice}</div>}

                <section className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
                    <div className="flex flex-col gap-3 border-b border-slate-800 p-4 md:flex-row md:items-center md:justify-between">
                        <input value={search} onChange={(event) => { setSearch(event.target.value); setSelected([]); }} placeholder={`Search ${isProducts ? "products, categories, outlets" : "categories, outlets"}`} className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500 md:max-w-sm" />
                        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-400">
                            <span>{selected.length} selected</span>
                            {selected.length > 0 && <>
                                <button type="button" onClick={() => { setBulkFields({ name: "", price: "", gstRate: "" }); setFormMode("bulk"); }} className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-200 hover:bg-slate-800">Bulk edit</button>
                                <button type="button" disabled={saving} onClick={() => handleBatchStatus("deactive")} className="rounded-lg border border-rose-500/40 px-3 py-1.5 text-rose-200 disabled:opacity-50">Archive</button>
                                <button type="button" disabled={saving} onClick={() => handleBatchStatus("active")} className="rounded-lg border border-emerald-500/40 px-3 py-1.5 text-emerald-200 disabled:opacity-50">Restore</button>
                            </>}
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] border-collapse text-left text-sm">
                            <thead className="bg-slate-950/70 text-xs uppercase text-slate-400"><tr>
                                <th className="p-3"><input aria-label="Select all visible rows" type="checkbox" checked={allVisibleSelected} onChange={toggleAllVisible} /></th>
                                <th className="p-3">{isProducts ? "Product" : "Category"}</th><th className="p-3">Restaurant</th>
                                {isProducts && <><th className="p-3">Category</th><th className="p-3 text-right">Price</th><th className="p-3 text-right">Product GST</th><th className="p-3 text-right">Category GST</th></>}
                                {!isProducts && <><th className="p-3 text-right">Category GST</th><th className="p-3 text-right">Active products</th></>}
                                <th className="p-3">Status</th><th className="p-3 text-right">Action</th>
                            </tr></thead>
                            <tbody className="divide-y divide-slate-800">
                                {loading ? <tr><td colSpan={isProducts ? 9 : 7} className="p-8 text-center text-slate-400">Loading catalog…</td></tr>
                                    : rows.length === 0 ? <tr><td colSpan={isProducts ? 9 : 7} className="p-8 text-center text-slate-400">No matching {isProducts ? "products" : "categories"}.</td></tr>
                                        : rows.map((row) => <tr key={selectionKey(row, kind)} className="hover:bg-slate-800/40">
                                            <td className="p-3"><input aria-label={`Select ${row.name}`} type="checkbox" checked={selected.includes(selectionKey(row, kind))} onChange={() => toggleSelection(row)} /></td>
                                            <td className="p-3 font-medium text-white">{row.name}</td><td className="p-3 text-slate-300">{row.restaurantName}</td>
                                            {isProducts && <><td className="p-3 text-slate-300">{row.categoryName || "—"}</td><td className="p-3 text-right tabular-nums">{money(row.price)}</td><td className="p-3 text-right">{row.gstRate}%</td><td className="p-3 text-right">{row.categoryGstRate === 0 ? <span className="text-amber-300">0% · Exempt</span> : `${row.categoryGstRate ?? "—"}%`}</td></>}
                                            {!isProducts && <><td className="p-3 text-right">{row.gstRate}%</td><td className="p-3 text-right">{row.activeProductCount}</td></>}
                                            <td className="p-3"><span className={`rounded-full px-2 py-1 text-xs ${row.status === "active" ? "bg-emerald-500/10 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>{row.status === "active" ? "Active" : "Archived"}</span></td>
                                            <td className="p-3 text-right"><button type="button" onClick={() => beginEdit(row)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-slate-200 hover:bg-slate-800">Edit</button></td>
                                        </tr>)}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {formMode && <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/75 p-4" role="presentation">
                <section role="dialog" aria-modal="true" aria-labelledby="catalog-form-title" className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 p-5 shadow-2xl">
                    <div className="flex items-start justify-between gap-4">
                        <div><p className="text-xs font-semibold uppercase tracking-widest text-cyan-300">Catalog management</p><h2 id="catalog-form-title" className="mt-1 text-xl font-semibold text-white">{formMode === "create" ? `Add ${isProducts ? "product" : "category"}` : formMode === "edit" ? `Edit ${isProducts ? "product" : "category"}` : `Bulk edit ${isProducts ? "products" : "categories"}`}</h2></div>
                        <button type="button" onClick={closeForm} className="rounded-md px-2 text-xl text-slate-400 hover:text-white" aria-label="Close dialog">×</button>
                    </div>

                    {formMode === "create" ? <form onSubmit={handleCreate} className="mt-5 space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">{editFields}</div>
                        <fieldset className="rounded-xl border border-slate-700 p-3">
                            <legend className="px-1 text-sm font-medium text-slate-300">Target restaurants</legend>
                            {restaurantFilter !== "all" ? <p className="text-sm text-slate-300">{restaurants.find((restaurant) => restaurant.id === Number(restaurantFilter))?.name}</p> : <div className="grid max-h-36 gap-2 overflow-y-auto sm:grid-cols-2">{restaurants.map((restaurant) => <label key={restaurant.id} className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={targetRestaurantIds.includes(restaurant.id)} onChange={() => toggleTargetRestaurant(restaurant.id)} />{restaurant.name}</label>)}</div>}
                        </fieldset>
                        {isProducts && targetRestaurantIds.map((restaurantId) => <label key={restaurantId} className="block text-sm text-slate-300">Category for {restaurants.find((restaurant) => restaurant.id === restaurantId)?.name}<select required value={categoryMappings[restaurantId] || ""} onChange={(event) => setCategoryMappings((current) => ({ ...current, [restaurantId]: event.target.value }))} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white"><option value="">Select category</option>{activeCategoriesForRestaurant(restaurantId).map((category) => <option key={category.categoryId} value={category.categoryId}>{category.name}</option>)}</select></label>)}
                        <div className="flex justify-end gap-2 border-t border-slate-800 pt-4"><button type="button" onClick={closeForm} className="rounded-lg border border-slate-700 px-4 py-2 text-slate-300">Cancel</button><button disabled={saving} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Create"}</button></div>
                    </form> : formMode === "edit" ? <form onSubmit={handleEdit} className="mt-5 space-y-4"><div className="grid gap-3 sm:grid-cols-2">{editFields}</div><p className="text-xs text-slate-500">Restaurant: {editingRow?.restaurantName}. Historical bills keep their saved item snapshots.</p><div className="flex justify-end gap-2 border-t border-slate-800 pt-4"><button type="button" onClick={closeForm} className="rounded-lg border border-slate-700 px-4 py-2 text-slate-300">Cancel</button><button disabled={saving} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button></div></form> : <form onSubmit={handleBatchEdit} className="mt-5 space-y-4"><p className="text-sm text-slate-400">Changes apply to {targetsPayload.length} selected rows across the filtered outlets. Leave a field blank to keep it unchanged.</p><div className="grid gap-3 sm:grid-cols-2"><label className="text-sm text-slate-300">Shared name<input value={bulkFields.name} onChange={(event) => setBulkFields({ ...bulkFields, name: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>{isProducts && <label className="text-sm text-slate-300">Shared price<input type="number" min="1" step="1" value={bulkFields.price} onChange={(event) => setBulkFields({ ...bulkFields, price: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label>}<label className="text-sm text-slate-300">Shared GST rate (%)<input type="number" min="0" max="100" step="1" value={bulkFields.gstRate} onChange={(event) => setBulkFields({ ...bulkFields, gstRate: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-white" /></label></div><div className="flex justify-end gap-2 border-t border-slate-800 pt-4"><button type="button" onClick={closeForm} className="rounded-lg border border-slate-700 px-4 py-2 text-slate-300">Cancel</button><button disabled={saving} className="rounded-lg bg-cyan-600 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Apply to selected"}</button></div></form>}
                </section>
            </div>}
        </main>
    );
}
